import moment from 'moment-timezone';

import { EMAIL_PATTERN, RPM_PATIENT_STATUS } from '../../constants';
import Strings from '../../Strings';
import {
  EMAIL_MAX_LENGTH,
  FIRST_NAME_MAX_LENGTH,
  INSURANCE_AND_BILLING_NOTES_MAX_LENGTH,
  LAST_NAME_MAX_LENGTH,
} from './fieldConstraints';
import { supportedLanguages } from '../language';
import isValidNpi, { hasValidNpiFormat } from '../npi';
import { OUTREACH_STATUS_ENUM, patientFieldsEnum } from '../rpmPatientFields';
import { ORG_FEATURES, orgFeatureEnabled } from '../userPermissions';
import { CONSENT_DRAFT_PROBLEMS, getConsentDraftProblems, getConsentTemplate, hasRecordedConsent } from '../consentTemplates';

const allTimeZones = moment.tz.names();

function validateExternalProvider(provider, property, propertyName) {
  if (!provider) {
    return [{ property, propertyName, errors: [], missing: true }];
  }

  const errors = [];
  if (!provider.firstName?.trim() || !provider.lastName?.trim()) {
    errors.push(Strings.externalProviderDirectory.nameRequired);
  }

  const npi = provider.npi?.trim();
  if (!npi) {
    errors.push(Strings.externalProviderDirectory.npiRequired);
  } else if (!isValidNpi(npi)) {
    errors.push(hasValidNpiFormat(npi) ? Strings.invalidNpiChecksum : Strings.invalidNpi);
  }

  if (!provider.directAddress?.trim() && !provider.faxNumber?.trim()) {
    errors.push(Strings.externalProviderDirectory.communicationChannelRequired);
  }

  return errors.length > 0 ? [{ property, propertyName, errors, missing: false }] : [];
}

export function validateAccessClinicalDetails(patient, enrollment, treatmentPlanRequired = false) {
  const errors = [];
  const requiredValues = [];
  if (treatmentPlanRequired) {
    requiredValues.push(['treatmentPlan', Strings.treatmentPlan, enrollment?.treatmentPlan]);
  }

  requiredValues.forEach(([property, propertyName, value]) => {
    if (!value || (typeof value === 'string' && !value.trim())) {
      errors.push({ property, propertyName, errors: [], missing: true });
    }
  });

  errors.push(...validateExternalProvider(patient?.primaryCareProvider, 'primaryCareProvider', Strings.primaryCareProvider));

  if (enrollment?.careUpdateConsent === undefined || enrollment?.careUpdateConsent === null) {
    errors.push({
      property: 'careUpdateConsent',
      propertyName: Strings.careUpdateConsent,
      errors: [],
      missing: true,
    });
  }

  return errors;
}

export function validateAccessRegistration(patient, enrollment) {
  const errors = [];
  if (!enrollment?.accessTrack) {
    errors.push({ property: 'accessTrack', propertyName: Strings.accessTrack, errors: [], missing: true });
  }
  if (enrollment?.referredByProvider) {
    errors.push(...validateExternalProvider(enrollment.referringProvider, 'referringProvider', Strings.referringProvider));
  }
  errors.push(...validateAccessParticipationConsent(enrollment));
  return errors.concat(validateAccessClinicalDetails(patient, enrollment));
}

// The consent to take part in ACCESS is the enrollment's consent checklist (CMS requires it before
// the eligibility check). A consent the enrollment already has stands.
function validateAccessParticipationConsent(enrollment) {
  if (!enrollment?.accessTrack || hasRecordedConsent(enrollment)) {
    return [];
  }
  const problems = getConsentDraftProblems(enrollment.consentDraft, getConsentTemplate(enrollment.accessTrack));
  if (problems.length === 0) {
    return [];
  }
  const messages = {
    [CONSENT_DRAFT_PROBLEMS.date]: Strings.consentChecklist.errorDate,
    [CONSENT_DRAFT_PROBLEMS.futureDate]: Strings.consentChecklist.errorFutureDate,
    [CONSENT_DRAFT_PROBLEMS.obtainedBy]: Strings.consentChecklist.errorObtainedBy,
    [CONSENT_DRAFT_PROBLEMS.items]: Strings.consentChecklist.errorItems,
  };
  return [{
    property: 'enrollmentConsents',
    propertyName: Strings.enrollmentConsent,
    errors: problems.filter(p => messages[p]).map(p => messages[p]),
    missing: problems.includes(CONSENT_DRAFT_PROBLEMS.status),
  }];
}

export const validateRpmPatient = (patient, conditions, facilities, stage, enrollment, athenaPatient) => {
  let errors = validateFirstName(patient.firstName, athenaPatient)
    .concat(validateLastName(patient.lastName, athenaPatient))
    .concat(validateMRN(patient.mrn))
    .concat(validatePatientId(patient.pharmacyPatientId))
    .concat(validatePhoneNoField(patient.textPhoneNo, 'textPhoneNo', stage, patient, athenaPatient))
    .concat(validateEmail(patient.email, patient.last_login))
    .concat(validateHomeAddress(patient.homeAddress, stage, enrollment))
    .concat(validateAddress(patient.address, stage, enrollment, athenaPatient))
    .concat(validateDOB(patient.dateOfBirth, athenaPatient))
    .concat(validateGender(patient.gender))
    .concat(validateLanguage(patient.language))
    .concat(validateTimezone(patient.timezone))
    .concat(validateTransplantDetails(patient.transplantDetails))
    .concat(validateInsurance(patient.insurance))
    .concat(validateConditions(patient.conditions, conditions, stage))
    .concat(validateFacility(patient.facilityId, facilities, stage))
    .concat(validateCenterDetails(patient.centerDetails))
    .concat(validateMBI(patient, stage, enrollment))
    .concat(validateTextOptOutStatus(patient, stage));

  if (enrollment?.registrationTypeSelectionRequired && !enrollment.registrationType) {
    errors.push({ property: 'registrationType', propertyName: Strings.enrollmentType, errors: [], missing: true });
    return errors;
  }

  if (enrollment?.accessRegistration) {
    return errors.concat(validateAccessRegistration(patient, enrollment));
  }

  errors = errors.concat(validateConsentForProvider(patient, stage))
    .concat(validateOutreachStatus(patient, stage))
    .concat(validateNextOutreachAt(patient, stage))
    .concat(validateTelevisit(patient));

  return errors;
};

export function removeNonErrors(errors) {
  return errors.filter(e => e.missing || e.errors?.length > 0);
}

export function validateFirstName(firstName, athenaPatient) {
  const ret = { property: 'firstName', propertyName: Strings.firstName, errors: [], missing: false };

  if (!firstName) {
    ret.missing = patientFieldsEnum.firstName.required();
  } else {
    if (firstName.length > FIRST_NAME_MAX_LENGTH) {
      ret.errors.push(Strings.formatString(Strings.capPatient.wrongFieldLengthFormat, FIRST_NAME_MAX_LENGTH));
    }
    if (athenaPatient && firstName !== athenaPatient.firstName) {
      ret.errors.push(Strings.mismatchBetweenPatientAndAthena);
    }
  }

  return removeNonErrors([ret]);
}

export function validateLastName(lastName, athenaPatient) {
  const ret = { property: 'lastName', propertyName: Strings.lastName, errors: [], missing: false };

  if (!lastName) {
    ret.missing = patientFieldsEnum.lastName.required();
  } else {
    if (lastName.length > LAST_NAME_MAX_LENGTH) {
      ret.errors.push(Strings.formatString(Strings.capPatient.wrongFieldLengthFormat, LAST_NAME_MAX_LENGTH));
    }
    if (athenaPatient && lastName !== athenaPatient.lastName) {
      ret.errors.push(Strings.mismatchBetweenPatientAndAthena);
    }
  }

  return removeNonErrors([ret]);
}

export function validateMRN(mrn) {
  const ret = { property: 'mrn', propertyName: Strings.capPatient.mrn, errors: [], missing: false };
  return removeNonErrors([ret]);
}

export function validatePatientId(id) {
  const ret = { property: 'pharmacyPatientId', propertyName: Strings.capPatient.patientId, errors: [], missing: false };
  if (!id) {
    ret.missing = patientFieldsEnum.mrn.required();
  }
  return removeNonErrors([ret]);
}

// More flexible pattern that accepts E.164 format with country code (e.g., +1234567890) or without
// Allows 7-15 digits total (E.164 standard)
const phoneNoPattern = new RegExp(/^(\+\d{1,3})?\d{7,15}$/);
export function validatePhoneNoField(number, field, stage, patient, athenaPatient) {
  const ret = { property: field, propertyName: Strings.mobilePhone, errors: [], missing: false };

  if (!number) {
    ret.missing = patientFieldsEnum[field].required(patient, stage);
  } else {
    if (!phoneNoPattern.test(number)) {
      ret.errors.push(Strings.formatString(Strings.capPatient.incorrectFieldFormat));
    }
    if (athenaPatient && number !== athenaPatient[field]) {
      ret.errors.push(Strings.mismatchBetweenPatientAndAthena);
    }
  }

  return removeNonErrors([ret]);
}

export function validatePhoneNo(number, field, isRequired) {
  const ret = { property: field, propertyName: Strings.mobilePhone, errors: [], missing: false };

  if (!number) {
    ret.missing = isRequired;
  } else if (!phoneNoPattern.test(number)) {
    ret.errors.push(Strings.formatString(Strings.capPatient.incorrectFieldFormat));
  }

  return removeNonErrors([ret]);
}

export function validateEmail(email, lastLogin, athenaPatient) {
  const ret = { property: 'email', propertyName: Strings.email, errors: [], missing: false };

  if (!email) {
    ret.missing = patientFieldsEnum.email.required({last_login: lastLogin});
  }

  if (email && !EMAIL_PATTERN.test(email)) ret.errors.push(Strings.formatString(Strings.capPatient.incorrectFieldFormat));
  if (email?.length > EMAIL_MAX_LENGTH)
    ret.errors.push(Strings.formatString(Strings.capPatient.wrongFieldLengthFormat, EMAIL_MAX_LENGTH));
  if (athenaPatient && email !== athenaPatient.email) {
    ret.errors.push(Strings.mismatchBetweenPatientAndAthena);
  }

  return removeNonErrors([ret]);
}

export function validateHomeAddress(address, stage, enrollment) {
  const ret = { property: 'homeAddress', propertyName: Strings.capPatient.address, errors: [], missing: false };
  if (!address) {
    ret.missing = patientFieldsEnum.homeAddress.required(undefined, stage, enrollment);
  }
  return removeNonErrors([ret]);
}

export function validateAddress(address, stage, enrollment, athenaPatient) {
  const ret = [];
  const isAddressRequired = patientFieldsEnum.address.required(undefined, stage, enrollment);
  if (!address) {
    ret.push({ property: 'address', propertyName: Strings.capPatient.address, errors: [], missing: isAddressRequired });
  } else {
    if (!address.street && isAddressRequired) {
      ret.push({ property: 'street', propertyName: Strings.streetAddress, category: 'address',errors: [], missing: true });
    }
    if (!address.city && isAddressRequired) {
      ret.push({ property: 'city', propertyName: Strings.esiPatientInfo.city, category: 'address', errors: [], missing: true });
    }
    if (!address.state && isAddressRequired) {
      ret.push({ property: 'state', propertyName: Strings.esiPatientInfo.state, category: 'address', errors: [], missing: true });
    }
    if (!address.zip && isAddressRequired) {
      ret.push({ property: 'zip', propertyName: Strings.esiPatientInfo.zip, category: 'address', errors: [], missing: true });
    }
    if (!address.country && isAddressRequired) {
      ret.push({ property: 'country', propertyName: Strings.esiPatientInfo.country, category: 'address', errors: [], missing: true });
    }
    if (athenaPatient) {
      if (address.street && address.street !== athenaPatient.address?.street) {
        ret.push({ property: 'street', propertyName: Strings.streetAddress, category: 'address', errors: [Strings.mismatchBetweenPatientAndAthena], missing: false });
      }
      if (address.city && address.city !== athenaPatient.address?.city) {
        ret.push({ property: 'city', propertyName: Strings.esiPatientInfo.city, category: 'address', errors: [Strings.mismatchBetweenPatientAndAthena], missing: false });
      }
      if (address.state && address.state !== athenaPatient.address?.state) {
        ret.push({ property: 'state', propertyName: Strings.esiPatientInfo.state, category: 'address', errors: [Strings.mismatchBetweenPatientAndAthena], missing: false });
      }
      if (address.zip && address.zip !== athenaPatient.address?.zip) {
        ret.push({ property: 'zip', propertyName: Strings.esiPatientInfo.zip, category: 'address', errors: [Strings.mismatchBetweenPatientAndAthena], missing: false });
      }
      if (address.country && address.country !== athenaPatient.address?.country) {
        ret.push({ property: 'country', propertyName: Strings.esiPatientInfo.country, category: 'address', errors: [Strings.mismatchBetweenPatientAndAthena], missing: false });
      }
      if (address.street2 && athenaPatient.address?.street2 && address.street2 !== athenaPatient.address?.street2) {
        ret.push({ property: 'street2', propertyName: Strings.streetAddressLine2, category: 'address', errors: [Strings.mismatchBetweenPatientAndAthena], missing: false });
      }
    }
  }
  return removeNonErrors(ret);
}

export function validateDOB(dob, athenaPatient) {
  const ret = { property: 'dateOfBirth', propertyName: Strings.dateOfBirth, errors: [], missing: false };
  if (!dob) {
    ret.missing = patientFieldsEnum.dateOfBirth.required();
  } else if (moment.isMoment(dob)) {
    if (!dob.isValid()) {
      ret.errors.push(Strings.formatString(Strings.capPatient.incorrectFieldFormat));
    }
    if (dob.isAfter(moment())) {
      ret.errors.push(Strings.formatString(Strings.errors.dateInTheFuture));
    }
    if (dob.year() < 1000) {
      ret.errors.push(Strings.formatString(Strings.capPatient.incorrectFieldFormat));
    }
  }
  if (athenaPatient && dob && athenaPatient.dateOfBirth) {
    if (!dob.isSame(athenaPatient.dateOfBirth, 'day')) {
      ret.errors.push(Strings.mismatchBetweenPatientAndAthena);
    }
  }

  return removeNonErrors([ret]);
}

export function validateTimezone(timezone) {
  const ret = { property: 'timezone', propertyName: Strings.timezone, errors: [], missing: false };
  if (!timezone) {
    ret.missing = patientFieldsEnum.timezone.required();
  }
  if (timezone && !allTimeZones.includes(timezone))
    ret.errors.push(Strings.formatString(Strings.capPatient.incorrectFieldValue, timezone));
  return removeNonErrors([ret]);
}

export function validateGender(gender) {
  const ret = { property: 'gender', propertyName: Strings.gender, errors: [], missing: false };

  if (!gender) {
    ret.missing = patientFieldsEnum.gender.required();
  } else if (!Object.values(Strings.genders).includes(gender)) {
    ret.errors.push(Strings.formatString(Strings.capPatient.incorrectFieldValue, gender));
  }

  return removeNonErrors([ret]);
}

export function validateLanguage(language) {
  const ret = { property: 'language', propertyName: Strings.language, errors: [], missing: false };

  if (!language) {
    ret.missing = patientFieldsEnum.language.required();
  } else {
    if (!supportedLanguages.map(lg => lg.id).includes(language.toLowerCase())) {
      ret.errors.push(Strings.formatString(Strings.capPatient.incorrectFieldValue, language));
    }
  }

  return removeNonErrors([ret]);
}

export function validateTransplantDetails(transplantDetails) {
  const ret = [];

  if (!transplantDetails) {
    return ret;
  }

  for (let i = 0; i < transplantDetails.length; i += 1) {
    if (!transplantDetails[i].transplantType) {
      break;
    }
    if (transplantDetails[i].transplantDate && !transplantDetails[i].transplantDate.isValid()) {
      ret.push({
        property: 'transplantDetails',
        propertyName: Strings.capPatient.transplantDate,
        index: i,
        field: 'transplantDate',
        errors: [Strings.formatString(Strings.capPatient.incorrectFieldFormat)],
        missing: false,
      });
    } else if (transplantDetails[i].transplantDate.isAfter(moment())) {
      ret.push({
        property: 'transplantDetails',
        propertyName: Strings.capPatient.transplantDate,
        index: i,
        field: 'transplantDate',
        errors: [Strings.formatString(Strings.errors.dateInTheFuture)],
        missing: false,
      });
    } else if (transplantDetails[i].transplantDate.year() < 1000) {
      ret.push({
        property: 'transplantDetails',
        propertyName: Strings.capPatient.transplantDate,
        index: i,
        field: 'transplantDate',
        errors: [Strings.formatString(Strings.capPatient.incorrectFieldFormat)],
        missing: false,
      });
    }

  }
  return removeNonErrors(ret);
}

export function validateInsurance(insurance) {
  const ret = [];
  if (!insurance) {
    return ret;
  }

  insurance.forEach((item, index) => {
    const notes = item?.insuranceAndBillingNotes;
    if (typeof notes === 'string' && Array.from(notes).length > INSURANCE_AND_BILLING_NOTES_MAX_LENGTH) {
      ret.push({
        property: 'insurance',
        propertyName: Strings.capPatient.insuranceAndBillingNotes,
        index,
        field: 'insuranceAndBillingNotes',
        errors: [
          Strings.formatString(
            Strings.capPatient.wrongFieldLengthFormat,
            INSURANCE_AND_BILLING_NOTES_MAX_LENGTH,
          ),
        ],
        missing: false,
      });
    }
  });

  return removeNonErrors(ret);
}

export function validateConditions(patientConditions, orgConditions) {
  if (!orgConditions || orgConditions.length === 0) {
    return [];
  }

  const ret = [];
  // patientConditions?.forEach(c => {
  //   ret.push({
  //     property: 'conditions',
  //     propertyName: Strings.conditions,
  //     index: 0,
  //     errors: validateConditionId(c, orgConditions),
  //     missing: patientFieldsEnum.conditions.required(),
  //   });
  // });
  return removeNonErrors(ret);
}

export function validateConditionId(condition, conditions) {
  const errors = [];
  if (condition && !conditions.some(d => d.id === condition || d.code?.toLowerCase() === condition?.toLowerCase()))
    errors.push(Strings.formatString(Strings.capPatient.incorrectFieldValue, condition));
  return errors;
}

export function validateFacility(facilityId, facilities) {
  const ret = { property: 'facilityId', propertyName: Strings.facility, errors: [], missing: false };
  if (!facilities || facilities.length === 0) {
    return removeNonErrors([ret]);
  }

  if (!facilityId) {
    ret.missing = patientFieldsEnum.facilityId.required();
  }
  if (facilityId && !facilities.some(d => d.id === facilityId)) {
    ret.errors.push(Strings.formatString(Strings.capPatient.incorrectFieldValue, facilityId));
  }
  return removeNonErrors([ret]);
}

const mbiPattern = /^[1-9][ACDEFGHJKMNPQRTUVWXY][0-9ACDEFGHJKMNPQRTUVWXY][0-9][ACDEFGHJKMNPQRTUVWXY][0-9ACDEFGHJKMNPQRTUVWXY][0-9][ACDEFGHJKMNPQRTUVWXY][ACDEFGHJKMNPQRTUVWXY][0-9]{2}$/;
export function validateMBI(patient, stage, enrollment) {
  const medicareBeneficiaryId = patient?.medicareBeneficiaryId;
  const ret = { property: 'medicareBeneficiaryId', propertyName: Strings.capPatient.medicareBeneficiaryId, errors: [], missing: false };
  if (!medicareBeneficiaryId) {
    ret.missing = patientFieldsEnum.medicareBeneficiaryId.required(patient, stage, enrollment);
  } else if (medicareBeneficiaryId && !mbiPattern.test(medicareBeneficiaryId)) {
    ret.errors.push(Strings.formatString(Strings.capPatient.incorrectFieldFormat));
  }
  return removeNonErrors([ret]);
}

export function validateCenterDetails(centerDetails) {
  const ret = [];

  if (!centerDetails) {
    return ret;
  }

  for (let i = 0; i < centerDetails.length; i += 1) {
    if (centerDetails[i].clinicContact && !phoneNoPattern.test(centerDetails[i].clinicContact)) {
      ret.push({
        property: 'centerDetails',
        propertyName: Strings.capPatient.centerDetails,
        index: i,
        field: 'clinicContact',
        errors: [Strings.formatString(Strings.capPatient.incorrectFieldFormat)],
        missing: false,
      });
    }
  }
  return removeNonErrors(ret);
}

export function validateConsentForProvider(patient, stage) {
  const extendedOutreachStatusEnabled = orgFeatureEnabled(ORG_FEATURES.use_outreach_status);
  const ret = { property: 'consentForProvider', propertyName: Strings.agreementToSpeakWithProvider, errors: [], missing: false };

  if (extendedOutreachStatusEnabled) {
    return [];
  }
  if (!patient.consentForProvider) {
    ret.missing = patientFieldsEnum.consentForProvider.required(patient, stage);
  }
  return removeNonErrors([ret]);
}

export const validateOutreachStatus = (patient, stage) => {
  const extendedOutreachStatusEnabled = orgFeatureEnabled(ORG_FEATURES.use_outreach_status);
  const ret = { property: 'outreachStatus', propertyName: Strings.widgets.patientStatus, errors: [], missing: false };

  if (!extendedOutreachStatusEnabled) {
    return [];
  }
  if (!patient.outreachStatus) {
    ret.missing = patientFieldsEnum.outreachStatus.required(patient, stage);
  } else {
    if (patient.outreachStatus && !Object.values(OUTREACH_STATUS_ENUM).some(s => s.value === patient.outreachStatus)) {
      ret.errors.push(Strings.formatString(Strings.capPatient.incorrectFieldValue, patient.outreachStatus));
    }
    if (patient.outreachStatus !== OUTREACH_STATUS_ENUM['RPM Order Approved'].value && stage === RPM_PATIENT_STATUS.enrolled) {
      ret.errors.push(Strings.formatString(Strings.outreachStatusForEnrollmentWarning, OUTREACH_STATUS_ENUM['RPM Order Approved'].label));
    }
  }
  return removeNonErrors([ret]);
}

export const validateNextOutreachAt = (patient) => {
  const extendedOutreachStatusEnabled = orgFeatureEnabled(ORG_FEATURES.use_outreach_status);
  const showNextOutreachDate = extendedOutreachStatusEnabled
    && patient?.outreachStatus
    && OUTREACH_STATUS_ENUM[patient.outreachStatus]?.showNextOutreachDate;
  const ret = { property: 'nextOutreachAt', propertyName: Strings.nextOutreachDate, errors: [], missing: false };

  if (!showNextOutreachDate) {
    return [];
  }
  if (patient.nextOutreachAt && !moment(patient.nextOutreachAt).isValid()) {
    ret.errors.push(Strings.formatString(Strings.capPatient.incorrectFieldFormat));
  }
  if (patient.nextOutreachAt && moment(patient.nextOutreachAt).isBefore(moment())) {
    ret.errors.push(Strings.formatString(Strings.errors.dateInThePast));
  }
  return removeNonErrors([ret]);
}

export function validateTelevisit(patient) {
  const ret = { property: 'initiateEnrollmentByProviderAt', propertyName: Strings.capPatient.televisitScheduledDate, errors: [], missing: false };
  if (patient.televisitChecked && !patient.initiateEnrollmentByProviderAt) {
    ret.missing = patientFieldsEnum.initiateEnrollmentByProviderAt.required(patient);
  }
  if (patient.initiateEnrollmentByProviderAt && moment(patient.initiateEnrollmentByProviderAt).unix() < moment().unix()) {
    ret.errors = [Strings.errors.dateInThePast];
  }
  return removeNonErrors([ret]);
}

function validateTextOptOutStatus(patient, stage) {
  const ret = { property: 'textOptOutStatus', propertyName: Strings.capPatient.consentForNotifications, errors: [], missing: false };
  if (patient.manual_sms_enabled === undefined || patient.sms_notice_enabled === undefined) {
    ret.missing = patientFieldsEnum.textOptOutStatus.required(patient, stage);
  }
  return removeNonErrors([ret]);
}